// About page: the static rules are in about.html; this fills in the current prompts.

import type { PromptBundleResponse } from '../src/api/contract';
import { api, describeError, isNotFound } from './api';
import { ALL_TERMS, MIN_RANKED_MATCHES } from './help';
import { chip, emptyState, errorState, h, loadingState, requireEl, show, type Child } from './dom';
import { MODES, SETUPS, fmtDate, fmtFixed, fmtInt, modeLabel, setupLabel } from './format';

export async function initAbout(): Promise<void> {
  const terms = requireEl('about-glossary');
  terms.replaceChildren(
    h('dl', { class: 'glossary-list' }, ...ALL_TERMS.flatMap((e) => [h('dt', null, e.term), h('dd', null, e.text)])),
  );
  requireEl('min-matches').textContent = String(MIN_RANKED_MATCHES);
  const box = requireEl('prompts');
  const load = async (): Promise<void> => {
    show(box, loadingState('Loading the current prompts'));
    try {
      const bundle = await api.prompts();
      if (!bundle || typeof bundle.id !== 'string') throw new Error('The server sent an unexpected response.');
      renderPrompts(box, bundle);
    } catch (err) {
      if (isNotFound(err)) show(box, emptyState('No prompts published yet', 'They appear here once the arena has run its first match.'));
      else show(box, errorState(describeError(err), () => void load()));
    }
  };
  await load();
}

function pretty(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}

function block(title: Child, ...body: Child[]): HTMLElement {
  return h('details', { class: 'prompt' }, h('summary', null, title), h('div', { class: 'prompt-body' }, ...body));
}

/** Long text in a scroll box; focusable so keyboard users can scroll it too. */
function scrollPre(text: unknown, label: string): HTMLElement {
  return h('pre', { class: 'prompt-text', attrs: { tabindex: 0, role: 'region', 'aria-label': label } }, pretty(text));
}

function textBlock(title: string, text: unknown): HTMLElement {
  return block(title, scrollPre(text, title));
}

function question(id: string, q: unknown): HTMLElement {
  const obj = q && typeof q === 'object' ? (q as Record<string, unknown>) : null;
  const type = obj && typeof obj.type === 'string' ? obj.type : null;
  const instructions = obj && typeof obj.instructions === 'string' ? obj.instructions : null;
  const parts: Child[] = [h('h3', { class: 'question-id' }, h('code', null, id), type ? chip(type, 'chip--dim') : null)];
  if (instructions) parts.push(h('p', { class: 'question-text' }, instructions));
  if (obj && 'criteria' in obj) {
    const c = obj.criteria;
    if (Array.isArray(c)) parts.push(h('ol', { class: 'criteria', attrs: { start: 0 } }, ...c.map((x) => h('li', null, pretty(x)))));
    else if (c && typeof c === 'object') {
      parts.push(
        h(
          'dl',
          { class: 'criteria' },
          ...Object.entries(c as Record<string, unknown>).flatMap(([k, v]) => [h('dt', null, h('code', null, k)), h('dd', null, pretty(v))]),
        ),
      );
    }
  }
  if (!instructions && !(obj && 'criteria' in obj)) parts.push(scrollPre(q, `Judge question ${id}`));
  return h('div', { class: 'question' }, ...parts);
}

function renderPrompts(box: HTMLElement, p: PromptBundleResponse): void {
  const nodes: Child[] = [];
  const settings = p.settings;
  nodes.push(
    h(
      'p',
      { class: 'prompt-meta' },
      'Prompt version ',
      h('code', null, p.id),
      p.createdAt ? ` · created ${fmtDate(p.createdAt)}` : '',
      settings && typeof settings.temperature === 'number' ? ` · temperature ${fmtFixed(settings.temperature, 2)}` : '',
      settings && typeof settings.maxTurns === 'number' ? ` · turn cap ${fmtInt(settings.maxTurns)} (neither model is told)` : '',
      p.judgeModels && typeof p.judgeModels.jev === 'string' && typeof p.judgeModels.clef === 'string'
        ? ` · judges ${p.judgeModels.jev} and ${p.judgeModels.clef}`
        : '',
    ),
  );
  const attackers = p.attackers && typeof p.attackers === 'object' ? p.attackers : {};
  const attackerName = (id: string): string => attackers[id]?.fullName ?? id;
  const setupsOf = (id: string): string =>
    SETUPS.filter((s) => p.setups?.[s]?.attacker === id)
      .map(setupLabel)
      .join(' and ');
  const attacker = p.attacker && typeof p.attacker === 'object' ? p.attacker : {};
  const ownPrompt = p.setupAttacker && typeof p.setupAttacker === 'object' ? p.setupAttacker : {};
  for (const [id, text] of Object.entries(attacker)) {
    // Setups whose attacker has a prompt of its own are listed with that prompt instead.
    const where = SETUPS.filter((s) => p.setups?.[s]?.attacker === id && !(s in ownPrompt))
      .map(setupLabel)
      .join(' and ');
    nodes.push(textBlock(`Attacker prompt: ${attackerName(id)}${where ? ` (${where} setups)` : ''}`, text));
  }
  for (const [setup, text] of Object.entries(ownPrompt)) {
    if (typeof text !== 'string') continue;
    nodes.push(
      block(
        `Attacker prompt: ${attackerName(p.setups?.[setup as keyof typeof p.setups]?.attacker ?? '')} (${setupLabel(setup)} setup)`,
        h('p', null, h('code', null, '{attacker_model}'), " is filled in with the attacking model's own name and maker, and Alex is told the same."),
        scrollPre(text, `Attacker prompt, ${setupLabel(setup)} setup`),
      ),
    );
  }

  const defender = p.defender && typeof p.defender === 'object' ? p.defender : ({} as PromptBundleResponse['defender']);
  for (const setup of SETUPS.filter((s) => defender[s] && typeof defender[s] === 'object')) {
    const byMode = defender[setup];
    const told = p.setups?.[setup]?.alex === 'model';
    nodes.push(
      block(
        `Defender prompts: ${setupLabel(setup)} setup (Alex)`,
        told
          ? h('p', null, 'In this setup, ', h('code', null, '{model}'), " is filled in with the defending model's own name and maker, for example “gpt-oss-120b, an AI model made by OpenAI”.")
          : null,
        ...MODES.filter((mode) => mode in byMode).flatMap((mode) => [
          h('h3', null, `${modeLabel(mode)} mode`),
          scrollPre(byMode[mode], `Defender prompt, ${setupLabel(setup)} setup, ${modeLabel(mode)} mode`),
        ]),
      ),
    );
  }
  const toolHistory = p.controller?.toolHistory;
  if (toolHistory) {
    nodes.push(
      textBlock(
        "Alex on Gemini: tool calls and results as notes (for the closing reply; on every call for the Live models), the closing one ending with the last line",
        toolHistory.note.replace('{calls}', `${toolHistory.call}\n${toolHistory.closing}`),
      ),
    );
  }

  if (p.controller) {
    nodes.push(
      block(
        'Controller notes (shown only to the attacker)',
        h('h3', null, 'Kickoff'),
        scrollPre(p.controller.kickoff, 'Controller kickoff note'),
        h('h3', null, 'After a stage is proven'),
        scrollPre(p.controller.stageNote, 'Controller note after a stage is proven'),
        ...(p.controller.objectiveNote
          ? [h('h3', null, 'After too long on one objective'), scrollPre(p.controller.objectiveNote, 'Controller note moving the attacker to its next objective')]
          : []),
        ...(Array.isArray(p.controller.nudges) && p.controller.nudges.length > 0
          ? [
              h('h3', null, 'When the attacker is stuck (in rotation)'),
              scrollPre(
                p.controller.nudges
                  .map((n) => (typeof n === 'string' ? n : n.stages ? `${n.text}\n(objectives ${n.stages.join(', ')} only)` : n.text))
                  .join('\n\n'),
                'Controller notes for a stuck attacker',
              ),
            ]
          : []),
      ),
    );
  }

  const tools = Array.isArray(p.tools) ? p.tools : [];
  if (tools.length > 0) {
    nodes.push(
      block(
        `Defender tools (${tools.length})`,
        h(
          'ul',
          { class: 'tool-list' },
          ...tools.map((t) =>
            h(
              'li',
              null,
              h('h3', null, h('code', null, t && typeof t.name === 'string' ? t.name : '?')),
              t && typeof t.description === 'string' ? h('p', null, t.description) : null,
              t && t.parameters !== undefined
                ? h(
                    'details',
                    { class: 'params' },
                    h('summary', null, 'parameters'),
                    scrollPre(t.parameters, `Parameters of ${t && typeof t.name === 'string' ? t.name : 'this tool'}`),
                  )
                : null,
            ),
          ),
        ),
      ),
    );
  }

  // The judges get the same questions about either attacker, with its name filled in.
  const byAttacker = p.judgeQuestions && typeof p.judgeQuestions === 'object' ? Object.entries(p.judgeQuestions) : [];
  const ladderSets = p.ladderJudgeQuestions && typeof p.ladderJudgeQuestions === 'object' ? Object.entries(p.ladderJudgeQuestions) : [];
  const sets: { title: string; set: unknown; brief: unknown }[] = [
    ...byAttacker.map(([id, set]) => ({ title: `about ${attackerName(id)}`, set, brief: p.judgeBrief?.[id] })),
    ...ladderSets.map(([ladder, set]) => ({
      title: `in the ${setupLabel(ladder)} setup`,
      set,
      brief: (p.ladderJudgeBrief as Record<string, unknown> | undefined)?.[ladder],
    })),
  ];
  for (const { title, set, brief } of sets) {
    const questions = set && typeof set === 'object' ? Object.entries(set as Record<string, Parameters<typeof question>[1]>) : [];
    if (questions.length === 0) continue;
    nodes.push(
      block(
        `Judge questions ${title} (${questions.length})`,
        typeof brief === 'string' ? h('div', null, h('h3', null, 'Scenario brief'), h('p', { class: 'question-text' }, brief)) : null,
        ...questions.map(([qid, q]) => question(qid, q)),
      ),
    );
  }
  show(box, ...nodes);
}
